import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatGridListModule } from '@angular/material/grid-list';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { MtxGridModule } from '@ng-matero/extensions/grid';
import { FmQuotationRequest, FmQuotation } from '@core/api';
import { SmartusComponentsModule } from '@affinis/smartus-components';
import { formatPrice } from '@shared/utils/price-formatter';

export interface PreviousOffersDialogData {
  title: string;
  noEntriesMessage: string;
  quotationRequests?: FmQuotationRequest[];
  quotations?: FmQuotation[];
  serviceLineColumns: any[];
  fmIncidentArchObj: any[];
  isDummyFrameworkContract?: boolean;
  equipmentByArchObjId?: Record<string, any[]>;
  equipmentColumns?: any[];
  hideRequestFromLabel?: boolean;
  hideNoQuotationsMessage?: boolean;
  highlightPurchasingDepartmentRows?: boolean;
}

interface GroupedLocation {
  archObjId: string;
  archObj: any;
  rows: any[];
  deviatingAddress?: string;
  furtherLocationInformation?: string;
}

interface QuotationWithGroupedLines extends FmQuotation {
  groupedLines: GroupedLocation[];
}

interface ExpandableQuotationRequest {
  quotationRequest?: FmQuotationRequest;
  groupedLocations: GroupedLocation[];
  quotations: QuotationWithGroupedLines[];
  offerDate?: Date;
  filteredEquipmentGridRowsByArchObjId: Record<string, any[]>;
}

@Component({
  selector: 'app-previous-offers-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatExpansionModule,
    MatTableModule,
    MatGridListModule,
    ReactiveFormsModule,
    TranslateModule,
    MtxGridModule,
    MatIconModule,
    SmartusComponentsModule,
  ],
  templateUrl: './previous-offers-dialog.component.html',
  styleUrls: ['./previous-offers-dialog.component.scss'],
})
export class PreviousOffersDialogComponent implements OnInit {
  expandableRequests: ExpandableQuotationRequest[] = [];
  displayedColumns: string[] = [];
  requestServiceLineColumns: any[] = [];
  quotationServiceLineColumns: any[] = [];
  panelExpandedState: Record<string, boolean> = {};
  equipmentGridColumns: any[] = [];
  equipmentGroupExpandedState: Record<string, boolean> = {};
  equipmentGridRowsByArchObjId: Record<string, any[]> = {};
  private archObjLookupPool: any[] = [];
  equipmentRowClassFormatter = {
    'equipment-parent-row': (row: any) => row?.__kind === 'parent',
    'equipment-parent-row--expanded': (row: any) =>
      row?.__kind === 'parent' &&
      this.isEquipmentGroupRowExpanded(row?.__groupKey || ''),
    'equipment-child-row': (row: any) => row?.__kind === 'child',
  };

  requestRowClassFormatter = {
    'purchasing-department-position-row': (row: any) =>
      this.data.highlightPurchasingDepartmentRows === true &&
      row?.isPurchasingDepartmentPosition === true,
  };

  @ViewChild('equipmentToggleCellTpl', { static: true })
  equipmentToggleCellTpl!: TemplateRef<any>;

  constructor(
    public dialogRef: MatDialogRef<PreviousOffersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PreviousOffersDialogData,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.setDisplayedColumns();
    this.initializeEquipmentGridColumns();
    this.rebuildEquipmentGridRows();
    this.buildAllFilteredEquipmentGridRows();
  }

  private initializeEquipmentGridColumns(): void {
    const columns = this.data.equipmentColumns || [];
    this.equipmentGridColumns = columns.map((column: any) => {
      const originalFormatter = column?.formatter;
      if (column?.field === 'equipmentId') {
        return {
          ...column,
          sortable: false,
          cellTemplate: this.equipmentToggleCellTpl,
          formatter: () => '\u00A0',
        };
      }
      return {
        ...column,
        formatter: (row: any) => {
          if (row?.__kind === 'parent') {
            if (column?.field === 'equipment') return row?.equipment || '';
            if (column?.field === 'quantity') return row?.quantity ?? '';
            return '\u00A0';
          }
          if (row?.__kind === 'child' && column?.field === 'equipment')
            return row?.equipment || '';
          if (typeof originalFormatter === 'function')
            return originalFormatter(row);
          return row?.[column?.field] ?? '';
        },
      };
    });
  }

  private rebuildEquipmentGridRows(): void {
    const map = this.data.equipmentByArchObjId || {};
    const next: Record<string, any[]> = {};
    for (const archObjId of Object.keys(map)) {
      next[archObjId] = this.buildEquipmentGridRowsForArchObj(archObjId);
    }
    this.equipmentGridRowsByArchObjId = next;
  }

  private buildAllFilteredEquipmentGridRows(): void {
    this.expandableRequests.forEach((request, index) => {
      this.rebuildFilteredEquipmentGridRowsForRequest(request, index);
    });
  }

  private rebuildFilteredEquipmentGridRowsForRequest(
    request: ExpandableQuotationRequest,
    requestIndex: number
  ): void {
    const map = this.data.equipmentByArchObjId || {};
    const offerDate = request.offerDate;
    const next: Record<string, any[]> = {};
    for (const archObjId of Object.keys(map)) {
      const allRows: any[] = map[archObjId] || [];
      const filtered = offerDate
        ? allRows.filter(row => {
            const cd = row?.createDate;
            if (cd && new Date(cd) > offerDate) return false;
            const dd = row?.deleteDate;
            if (dd && new Date(dd) < offerDate) return false;
            return true;
          })
        : allRows;
      next[archObjId] = this.buildEquipmentGridRowsForArchObj(
        archObjId,
        filtered,
        requestIndex
      );
    }
    request.filteredEquipmentGridRowsByArchObjId = next;
  }

  private buildEquipmentGridRowsForArchObj(
    archObjId: string,
    flatRows?: any[],
    requestIndex?: number
  ): any[] {
    const rows: any[] = [];
    const columns = this.data.equipmentColumns || [];
    for (const displayRow of this.buildEquipmentDisplayRows(
      archObjId,
      flatRows
    )) {
      if (displayRow.kind === 'single') {
        rows.push({
          __kind: 'single',
          __rowKey: displayRow.key,
          ...displayRow.row,
        });
        continue;
      }
      const groupKey = displayRow.key;
      const expanded = this.isEquipmentGroupRowExpanded(groupKey);
      const parentRow: any = {
        __kind: 'parent',
        __groupKey: groupKey,
        __rowKey: groupKey,
        ...(requestIndex !== undefined ? { __requestIndex: requestIndex } : {}),
      };
      for (const col of columns) {
        if (col?.field) parentRow[col.field] = '';
      }
      parentRow.equipmentId = '';
      parentRow.equipment = `${this.translateService.instant(
        'taskmodule.task.offer.collective_equipment'
      )} ${displayRow.parentEquipment || ''}`.trim();
      parentRow.quantity =
        (displayRow.children || []).reduce(
          (sum: number, child: any) =>
            sum + (parseFloat(String(child?.quantity)) || 0),
          0
        ) || '';
      rows.push(parentRow);
      if (expanded) {
        for (const child of displayRow.children || []) {
          rows.push({
            __kind: 'child',
            __groupKey: groupKey,
            __rowKey: `${groupKey}__child__${child?.id || ''}`,
            ...child,
            equipment: `${child?.equipment || ''}`.trim(),
          });
        }
      }
    }
    return rows;
  }

  private buildEquipmentDisplayRows(
    archObjId: string,
    flatRows?: any[]
  ): any[] {
    const rows = flatRows ?? this.getEquipmentRowsForArchObj(archObjId);
    const displayRows: any[] = [];
    const parentGroups = new Map<string, any>();
    rows.forEach((row: any, index: number) => {
      const parentEquipment = String(row?.parentEquipment || '').trim();
      if (!parentEquipment) {
        displayRows.push({
          kind: 'single',
          key: `${archObjId}__single__${index}__${row?.equipmentId || ''}`,
          row,
        });
        return;
      }
      const groupKey = `${archObjId}__parent__${parentEquipment}`;
      let groupRow = parentGroups.get(groupKey);
      if (!groupRow) {
        groupRow = {
          kind: 'parent',
          key: groupKey,
          parentEquipment,
          children: [],
        };
        parentGroups.set(groupKey, groupRow);
        displayRows.push(groupRow);
      }
      groupRow.children?.push(row);
    });
    return displayRows;
  }

  private initializeData(): void {
    this.archObjLookupPool = [...(this.data.fmIncidentArchObj || [])];

    if ((this.data.quotations || []).length > 0) {
      const sortedQuotations = [...(this.data.quotations || [])].sort(
        (a, b) =>
          new Date(b.changeDate || 0).getTime() -
          new Date(a.changeDate || 0).getTime()
      );

      this.archObjLookupPool.push(
        ...sortedQuotations.flatMap(
          quotation => quotation?.incident?.fmIncidentArchObj || []
        )
      );
      this.archObjLookupPool.push(
        ...sortedQuotations.flatMap(quotation =>
          this.collectIncidentArchObjsFromQuotation(quotation)
        )
      );

      this.expandableRequests = sortedQuotations.map(quotation => {
        const quotationRequest =
          this.resolveQuotationRequestForQuotation(quotation);
        const requestLines = quotationRequest?.fmQuotationRequestLine || [];

        return {
          quotationRequest,
          groupedLocations: quotationRequest
            ? this.groupLocationsByArchObj(requestLines)
            : [],
          quotations: [
            {
              ...quotation,
              groupedLines: this.groupQuotationLinesByArchObj(
                quotation.fmQuotationLine || [],
                requestLines
              ),
            },
          ],
          offerDate: quotation.changeDate
            ? new Date(quotation.changeDate)
            : undefined,
          filteredEquipmentGridRowsByArchObjId: {},
        };
      });
    } else {
      const sortedRequests = [...(this.data.quotationRequests || [])].sort(
        (a, b) =>
          new Date(b.changeDate || 0).getTime() -
          new Date(a.changeDate || 0).getTime()
      );

      this.expandableRequests = sortedRequests.map(request => {
        const requestLines = request.fmQuotationRequestLine || [];
        const quotations: QuotationWithGroupedLines[] = (
          request.fmQuotation || []
        ).map(quotation => ({
          ...quotation,
          groupedLines: this.groupQuotationLinesByArchObj(
            quotation.fmQuotationLine || [],
            requestLines
          ),
        }));

        return {
          quotationRequest: request,
          groupedLocations: this.groupLocationsByArchObj(requestLines),
          quotations,
          offerDate: request.changeDate
            ? new Date(request.changeDate)
            : undefined,
          filteredEquipmentGridRowsByArchObjId: {},
        };
      });
    }

    this.requestServiceLineColumns = this.data.serviceLineColumns || [];
    this.quotationServiceLineColumns = this.mapQuotationServiceLineColumns(
      this.requestServiceLineColumns
    );
  }

  private resolveQuotationRequestForQuotation(
    quotation: FmQuotation
  ): FmQuotationRequest | undefined {
    const requestCandidates = this.collectQuotationRequestCandidates(quotation);
    const requestId = quotation?.quotationRequestId;

    if (requestId && requestCandidates.length > 0) {
      const matchedById = requestCandidates.find(
        request => request?.id === requestId
      );
      if (matchedById) {
        return matchedById;
      }
    }

    if (requestCandidates.length === 1) {
      return requestCandidates[0];
    }

    return undefined;
  }

  private collectQuotationRequestCandidates(
    quotation: FmQuotation | any
  ): FmQuotationRequest[] {
    const candidateBuckets = [
      quotation?.incident?.fmQuotationRequest,
      quotation?.fmQuotationRequest,
      quotation?.quotationRequests,
    ];

    const flattenedFromBuckets = candidateBuckets
      .flatMap(bucket => (Array.isArray(bucket) ? bucket : []))
      .filter(Boolean) as FmQuotationRequest[];

    const singleCandidates = [quotation?.quotationRequest].filter(
      Boolean
    ) as FmQuotationRequest[];

    const byId = new Map<string, FmQuotationRequest>();
    const withoutId: FmQuotationRequest[] = [];

    [...flattenedFromBuckets, ...singleCandidates].forEach(request => {
      const key = this.normalizeIdentifier(request?.id);
      if (key) {
        if (!byId.has(key)) {
          byId.set(key, request);
        }
        return;
      }
      withoutId.push(request);
    });

    return [...byId.values(), ...withoutId];
  }

  private collectIncidentArchObjsFromQuotation(
    quotation: FmQuotation | any
  ): any[] {
    const directSources = [
      ...(quotation?.incident?.fmIncidentArchObj || []),
      ...(quotation?.fmIncidentArchObj || []),
    ];

    const fromRequests = this.collectQuotationRequestCandidates(
      quotation
    ).flatMap(request => request?.incident?.fmIncidentArchObj || []);

    const fromRequestLines = this.collectQuotationRequestCandidates(quotation)
      .flatMap(request => request?.fmQuotationRequestLine || [])
      .map(line => line?.archObj)
      .filter(Boolean);

    return [...directSources, ...fromRequests, ...fromRequestLines];
  }

  private mapQuotationServiceLineColumns(columns: any[]): any[] {
    return (columns || []).map(column => {
      if (column?.field === 'unitPrice') {
        return {
          ...column,
          formatter: (row: any) =>
            `${formatPrice(parseFloat(row?.unitPrice) || 0)} €`,
        };
      }

      if (column?.field === 'totalPrice') {
        return {
          ...column,
          formatter: (row: any) => {
            const quantity = parseFloat(String(row?.quantity)) || 0;
            const unitPrice = parseFloat(String(row?.unitPrice)) || 0;
            return `${formatPrice(quantity * unitPrice)} €`;
          },
        };
      }

      return column;
    });
  }

  private groupLocationsByArchObj(lines: any[]): GroupedLocation[] {
    const grouped = new Map<string, GroupedLocation>();

    lines.forEach(line => {
      const archObjId = line.archObjId || 'Unknown';
      if (!grouped.has(archObjId)) {
        const resolvedArchObj = this.resolveArchObjById(archObjId);
        const archObj = resolvedArchObj || line?.archObj || null;
        grouped.set(archObjId, {
          archObjId,
          archObj,
          rows: [],
          deviatingAddress: line.deviatingAddress || archObj?.deviatingAddress,
          furtherLocationInformation:
            line.furtherLocationInformation ||
            archObj?.furtherLocationInformation,
        });
      }
      const group = grouped.get(archObjId)!;
      if (!group.deviatingAddress && line.deviatingAddress) {
        group.deviatingAddress = line.deviatingAddress;
      }
      if (
        !group.furtherLocationInformation &&
        line.furtherLocationInformation
      ) {
        group.furtherLocationInformation = line.furtherLocationInformation;
      }

      group.rows.push(line);
    });

    const result = this.sortGroupedLocationsByArchObj(
      Array.from(grouped.values()).map(group => ({
        ...group,
        rows: this.sortLinesByRecordNumber(group.rows),
      }))
    );
    return result;
  }

  private groupQuotationLinesByArchObj(
    lines: any[],
    requestLines: any[] = []
  ): GroupedLocation[] {
    const grouped = new Map<string, GroupedLocation>();
    const fallbackByArchObj = new Map<
      string,
      { deviatingAddress?: string; furtherLocationInformation?: string }
    >();

    (requestLines || []).forEach(requestLine => {
      const archObjId = requestLine.archObjId || 'Unknown';
      const existing = fallbackByArchObj.get(archObjId);
      fallbackByArchObj.set(archObjId, {
        deviatingAddress:
          existing?.deviatingAddress ?? requestLine.deviatingAddress,
        furtherLocationInformation:
          existing?.furtherLocationInformation ??
          requestLine.furtherLocationInformation,
      });
    });

    lines.forEach(line => {
      const archObjId = line.archObjId || 'Unknown';
      const fallback = fallbackByArchObj.get(archObjId);

      if (!grouped.has(archObjId)) {
        const resolvedArchObj = this.resolveArchObjById(archObjId);
        const archObj = resolvedArchObj || line?.archObj || null;
        grouped.set(archObjId, {
          archObjId,
          archObj,
          rows: [],
          deviatingAddress:
            line.deviatingAddress ||
            fallback?.deviatingAddress ||
            archObj?.deviatingAddress,
          furtherLocationInformation:
            line.furtherLocationInformation ||
            fallback?.furtherLocationInformation ||
            archObj?.furtherLocationInformation,
        });
      }
      const group = grouped.get(archObjId)!;
      if (!group.deviatingAddress && line.deviatingAddress) {
        group.deviatingAddress = line.deviatingAddress;
      }
      if (
        !group.furtherLocationInformation &&
        line.furtherLocationInformation
      ) {
        group.furtherLocationInformation = line.furtherLocationInformation;
      }

      group.rows.push(line);
    });

    const result = this.sortGroupedLocationsByArchObj(
      Array.from(grouped.values()).map(group => ({
        ...group,
        rows: this.sortLinesByRecordNumber(group.rows),
      }))
    );
    return result;
  }

  private resolveArchObjById(archObjId: string): any {
    const targetId = this.normalizeIdentifier(archObjId);
    const matches = (this.archObjLookupPool || []).filter((obj: any) => {
      const directIds = [obj?.id, obj?.archObjId];
      const nestedIds = [obj?.archObj?.id, obj?.archObj?.archObjId];
      const buildingIds = [obj?.building?.id, obj?.building?.archObjId];
      const allIds = [...directIds, ...nestedIds, ...buildingIds]
        .map(value => this.normalizeIdentifier(value))
        .filter(Boolean);

      return allIds.includes(targetId);
    });

    if (matches.length === 0) {
      return null;
    }

    const firstMatch = matches[0];
    return firstMatch?.archObj || firstMatch;
  }

  private normalizeIdentifier(value: any): string {
    return String(value || '')
      .trim()
      .replace(/[{}]/g, '')
      .toLowerCase();
  }

  private sortGroupedLocationsByArchObj(
    groups: GroupedLocation[]
  ): GroupedLocation[] {
    return [...(groups || [])].sort((a, b) =>
      String(a?.archObjId || '').localeCompare(
        String(b?.archObjId || ''),
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        }
      )
    );
  }

  private sortLinesByRecordNumber<
    T extends { recordNumber?: number | string | null },
  >(lines: T[]): T[] {
    return [...(lines || [])].sort(
      (a, b) => (Number(a.recordNumber) || 0) - (Number(b.recordNumber) || 0)
    );
  }

  private setDisplayedColumns(): void {
    if (
      this.data.serviceLineColumns &&
      this.data.serviceLineColumns.length > 0
    ) {
      this.displayedColumns = this.data.serviceLineColumns.map(
        col => col.field
      );
    } else {
      this.displayedColumns = [
        'recordNumber',
        'description',
        'quantity',
        'unitPrice',
        'totalPrice',
      ];
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getEquipmentRowsForArchObj(archObjId: string): any[] {
    const map = this.data.equipmentByArchObjId;
    if (!map) return [];
    return map[archObjId] || [];
  }

  getEquipmentGridRowsForArchObj(archObjId: string): any[] {
    return this.equipmentGridRowsByArchObjId[archObjId] || [];
  }

  isParentEquipmentRow(row: any): boolean {
    return row?.__kind === 'parent';
  }

  getEquipmentIdDisplayValue(row: any): string {
    return row?.equipmentId || '';
  }

  isEquipmentGroupRowExpanded(groupKey: string): boolean {
    return !!this.equipmentGroupExpandedState[groupKey];
  }

  onEquipmentToggleClick(row: any, event: Event): void {
    event.stopPropagation();
    const groupKey = row?.__groupKey || '';
    this.equipmentGroupExpandedState[groupKey] =
      !this.equipmentGroupExpandedState[groupKey];
    const archObjId = groupKey.split('__')?.[0];
    const requestIndex = row?.__requestIndex;
    if (requestIndex !== undefined && this.expandableRequests[requestIndex]) {
      const request = this.expandableRequests[requestIndex];
      const offerDate = request.offerDate;
      const allRows: any[] =
        (this.data.equipmentByArchObjId || {})[archObjId] || [];
      const filtered = offerDate
        ? allRows.filter(r => {
            const cd = r?.createDate;
            if (cd && new Date(cd) > offerDate) return false;
            const dd = r?.deleteDate;
            if (dd && new Date(dd) < offerDate) return false;
            return true;
          })
        : allRows;
      request.filteredEquipmentGridRowsByArchObjId = {
        ...request.filteredEquipmentGridRowsByArchObjId,
        [archObjId]: this.buildEquipmentGridRowsForArchObj(
          archObjId,
          filtered,
          requestIndex
        ),
      };
    } else {
      this.equipmentGridRowsByArchObjId = {
        ...this.equipmentGridRowsByArchObjId,
        [archObjId]: this.buildEquipmentGridRowsForArchObj(archObjId),
      };
    }
  }

  getRequestChangeDate(request: FmQuotationRequest): string {
    if (!request.changeDate) {
      return '';
    }
    return this.formatDateToGermanPattern(request.changeDate);
  }

  getQuotationDate(quotation: FmQuotation): string {
    if (!quotation.changeDate) {
      return '';
    }
    return this.formatDateToGermanPattern(quotation.changeDate);
  }

  private formatDateToGermanPattern(value: string | Date): string {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  private getLineTotal(line: any, applyDummyRule = false): number {
    const quantity = parseFloat(String(line?.quantity)) || 0;
    const unitPrice = parseFloat(String(line?.unitPrice)) || 0;

    if (applyDummyRule && this.data.isDummyFrameworkContract) {
      return line?.priceNegotiated ? quantity * unitPrice : 0;
    }

    return quantity * unitPrice;
  }

  calculateLocationSum(group: GroupedLocation): string {
    const total = (group.rows || []).reduce((sum, line) => {
      return sum + this.getLineTotal(line, true);
    }, 0);
    return formatPrice(total);
  }

  calculateRequestTotal(request: FmQuotationRequest): string {
    const total = (request.fmQuotationRequestLine || []).reduce((sum, line) => {
      return sum + this.getLineTotal(line, true);
    }, 0);
    return formatPrice(total);
  }

  calculateQuotationLocationSum(group: GroupedLocation): string {
    const total = (group.rows || []).reduce((sum, line) => {
      return sum + this.getLineTotal(line, false);
    }, 0);
    return formatPrice(total);
  }

  calculateQuotationTotal(quotation: FmQuotation): string {
    const total = (quotation.fmQuotationLine || []).reduce((sum, line) => {
      return sum + this.getLineTotal(line, false);
    }, 0);
    return formatPrice(total);
  }

  isPanelExpanded(panelKey: string): boolean {
    return !!this.panelExpandedState[panelKey];
  }

  setPanelExpanded(panelKey: string, expanded: boolean): void {
    this.panelExpandedState[panelKey] = expanded;
  }
}
